import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Card, CardActionArea, CardContent, Chip,
  Typography, Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CodeIcon from "@mui/icons-material/Code";
import StudyGuideGr10 from "./StudyGuideGr10";
import StudyGuideLU5Gr10 from "./StudyGuideLU5Gr10";

type View = "hub" | "lu14" | "lu5";

interface Resource {
  id: View;
  title: string;
  subtitle: string;
  description: string;
  chips: string[];
  icon: React.ReactNode;
  color: string;
  accent: string;
}

const RESOURCES: Resource[] = [
  {
    id: "lu14",
    title: "LU 1 – 4  Study Guide",
    subtitle: "Data Representation · Hardware · Software",
    description:
      "Interactive study guide covering number systems, data storage, CPU architecture, memory, secondary storage, operating systems, and software licensing. Includes 80+ flashcards and 12 practice exercises.",
    chips: ["21 sections", "80+ flashcards", "Quiz mode"],
    icon: <MenuBookIcon sx={{ fontSize: 40 }} />,
    color: "#fff8f0",
    accent: "#b45309",
  },
  {
    id: "lu5",
    title: "LU 5  Solution Development",
    subtitle: "Algorithms · Pseudocode · Flowcharts · Java",
    description:
      "Full textbook on computational thinking and Java programming. Covers IPO model, pseudocode, flowcharts, variables, arithmetic, Math.random(), if/else, loops, and code tracing — with worked examples throughout.",
    chips: ["12 chapters", "44 flashcards", "Quiz mode", "Java"],
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    color: "#fdf8ff",
    accent: "#6d28d9",
  },
];

export default function Gr10ContentHub() {
  const [view, setView] = useState<View>("hub");
  const navigate = useNavigate();

  if (view === "lu14") return <StudyGuideGr10 onBack={() => setView("hub")} />;
  if (view === "lu5")  return <StudyGuideLU5Gr10 onBack={() => setView("hub")} />;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafaf8", pb: 8 }}>

      {/* Header */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "white",
          px: { xs: 2, sm: 4 },
          py: 3,
          mb: 4,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ color: "white", opacity: 0.85, mb: 1.5, "&:hover": { opacity: 1 } }}
          size="small"
        >
          Dashboard
        </Button>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Grade 10 Information Technology
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          Select a resource below to open it.
        </Typography>
      </Box>

      {/* Resource cards */}
      <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, sm: 4 } }}>
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", letterSpacing: 2, mb: 2, display: "block" }}
        >
          Study Materials
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
          }}
        >
          {RESOURCES.map(r => (
            <Card
              key={r.id}
              elevation={2}
              sx={{
                borderTop: `4px solid ${r.accent}`,
                borderRadius: 2,
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
              }}
            >
              <CardActionArea onClick={() => setView(r.id)} sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3, bgcolor: r.color }}>
                  <Box sx={{ color: r.accent, mb: 1.5 }}>{r.icon}</Box>
                  <Typography variant="h6" fontWeight={700} gutterBottom sx={{ lineHeight: 1.3 }}>
                    {r.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: r.accent, fontWeight: 600, display: "block", mb: 1 }}>
                    {r.subtitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {r.description}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {r.chips.map(c => (
                      <Chip
                        key={c}
                        label={c}
                        size="small"
                        sx={{
                          bgcolor: `${r.accent}18`,
                          color: r.accent,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Future resources placeholder */}
        <Box
          sx={{
            mt: 6,
            p: 3,
            borderRadius: 2,
            border: "2px dashed",
            borderColor: "divider",
            textAlign: "center",
            color: "text.disabled",
          }}
        >
          <Typography variant="body2">
            More resources — past papers, notes, and wiki articles — will appear here as they are added.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
